/**
 * This file is responsible for handling the service calls for the account
 */
import { callAPI, chameau, GATEWAY_PROXY, METHODS, DEFAULT_LIST_PARAMS, amIImpersonating } from "utils/apiUtils";
import {
	cleanPostHogId, identify,
	SEGMENT_EVENTS,
	segmentCondition,
	segmentPayloads,
	trackSegmentEvent,
} from "assets/js/const/SegmentEvents";
import CakeStore from "../store";
import { posthog } from "posthog-js";
import moment from "moment";
import { getVerisoulVerdict } from "../utils/verisoul";


export function whoami(
	options = {},
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + "/whoami",
			method: METHODS.get,
			body: options,
		},
		successCallback,
		failureCallback
	);
}

export function getAccountService(
	options = { id: "", useRootCreds: false },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + "/accounts/" + options.id,
			method: METHODS.get,
			query: {
				useRootCreds: options.useRootCreds || false
			},
		},
		successCallback,
		failureCallback
	);
}

export function listAccountService(
	options = { useRootCreds: false, useImpersonationTree: false, ...DEFAULT_LIST_PARAMS },
	successCallback = () => { },
	failureCallback = () => { }
) {

	const { useRootCreds, ...rest } = options;

	callAPI(
		{
			url: GATEWAY_PROXY + "/accounts",
			method: METHODS.get,
			query: {
				useRootCreds: useRootCreds || false,
				...chameau(rest)
			},
			useImpersonationTree: options.useImpersonationTree
		},
		successCallback,
		failureCallback
	);
}

export function confirmAccountService(
	options = { accountId: "", confirmation: "" },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + `/accounts/${options.accountId}/confirm`,
			method: METHODS.post,
			body: { confirmation: options.confirmation },
		},
		(data) => {

			identify(data.email, { accountConfirmed: true });
			trackSegmentEvent(SEGMENT_EVENTS.ACCOUNT_CONFIRMED, data.data);
			successCallback(data);
		},
		failureCallback
	);
}

export function createAccountService(
	options = { account: {} },
	successCallback = () => { },
	failureCallback = () => { }
) {
	const { account, ...rest } = options;
	callAPI(
		{
			url: GATEWAY_PROXY + "/accounts",
			method: METHODS.post,
			body: account,
			query: { ...chameau(rest) },
		},
		successCallback,
		failureCallback
	);
}

export function updateAccountService(
	options = { accountId: null, account: {} },
	successCallback = () => { },
	failureCallback = () => { }
) {
	const accountId = CakeStore.getState().global.account?.id;

	callAPI(
		{
			url: GATEWAY_PROXY + `/accounts/${accountId}`,
			method: METHODS.patch,
			body: options.account,
		},
		(data) => {
			if (segmentCondition(SEGMENT_EVENTS.GROUP)) {
				posthog.group(
					"account",
					data.data.id,
					segmentPayloads(SEGMENT_EVENTS.GROUP, data.data)
				);
			}
			successCallback(data);
		},
		failureCallback
	);
}

export function getAccountStatsService(
	options = { startTime: null, endTime: null },
	successCallback = () => { },
	failureCallback = () => { }
) {
	const accountId = CakeStore.getState().global.account?.id;

	if (parseInt(accountId) === 1) {
		successCallback({
			"data": {
				"active_contacts": 0,
				"sent_emails": 0,
				"sent_campaign_emails": 0,
				"sent_action_emails": 0,
				"sent_email_emails": 0,
				"open_rate": 0,
				"bounce_rate": 0,
				"click_rate": 0,
				"spam_rate": 0,
				"unsubscribe_rate": 0,
				"emails_usage": 0,
				"contacts_usage": 0,
				"bounces_hard": 0,
				"bounce_hard_rate": 0,
				"spams": 0,
				"current_lists": 0
			}
		});
		return;
	}
	
	callAPI(
		{
			url: GATEWAY_PROXY + "/reports/accounts/" + accountId,
			method: METHODS.get,
			query: chameau(options),
		},
		successCallback,
		failureCallback
	);
}

export function createAccountAndCampaignService(
	options = {},
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + "/accountAndCampaign",
			method: METHODS.post,
			body: options,
		},
		(data) => {
			trackSegmentEvent(SEGMENT_EVENTS.ACCOUNT_CREATED, data);
			successCallback(data);
		},
		failureCallback
	);
}

export function verifyAccountAndCampaignService(
	options = { accountId: "", waiterId: "", pinCode: "" },
	successCallback = () => { },
	failureCallback = () => { }
) {
	const { accountId, waiterId, pinCode, ...restOptions } = options;
	callAPI(
		{
			url:
				GATEWAY_PROXY +
				`/accountAndCampaign/${accountId}/waiter/${waiterId}/confirm-pin/${pinCode}`,
			method: METHODS.get,
			query: chameau(restOptions),
		},
		(data) => {
			trackSegmentEvent(SEGMENT_EVENTS.ACCOUNT_CONFIRMED, data);
			successCallback(data);
		},
		failureCallback
	);
}

export function resendConfirmationEmailAccountAndCampaignService(
	options = { accountId: "", waiterId: "" },
	successCallback = () => { },
	failureCallback = () => { }
) {
	const { accountId, waiterId, ...restOptions } = options;
	callAPI(
		{
			url:
				GATEWAY_PROXY +
				`/accountAndCampaign/${accountId}/waiter/${waiterId}/resend`,
			query: chameau(restOptions),
		},
		successCallback,
		failureCallback
	);
}

export function resendConfirmationEmailAccountService(
	options = { email: "" },
	successCallback = () => { },
	failureCallback = () => { }
) {
	const { email, ...rest } = options;
	callAPI(
		{
			url: GATEWAY_PROXY + "/accounts/resend-verification-email",
			method: METHODS.post,
			body: { email: email },
			query: {
				...chameau(rest),
				account_id: CakeStore.getState()?.global?.account?.id,
			},
		},
		(data) => {
			successCallback(data);
		},
		failureCallback
	);
}


export function convertToOrganizationService(
	options = { id: "" },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + `/accounts/${options.id || "self"}/convert-to-organization`,
			method: METHODS.post,
			query: {
				account_id: CakeStore.getState()?.global?.account?.id,
			},
			body: amIImpersonating() ? { migrate_owner: false } : undefined
		},
		(data) => {
			trackSegmentEvent(SEGMENT_EVENTS.ACCOUNT_CONVERTED_ORGANIZATION, {});
			successCallback(data);
		},
		failureCallback
	);
}

export function getVerisoulAccount(
	options = { id: undefined },
	successCallback = () => { },
	failureCallback = () => { }
) {
	callAPI(
		{
			url: GATEWAY_PROXY + `/verisoul`,
			method: METHODS.get,
			query: {
				id: options.id
			}
		},
		successCallback,
		failureCallback
	);
}